import { InstallmentStatusEnum, type Installment } from "@/generated/types";
import { TableRow, TableCell } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import Currency from "../Currency";
import DateDisplay from "../DateDisplay";
import MarkInstallmentAsPaidPopover from "./MarkInstallmentAsPaidPopover";
import type { MutationCallbacks } from "@/hooks/useMarkInstallmentAsPaid";

interface InstallmentRowProps {
  installment: Installment;
  showAdminActions?: boolean;
  onMarkAsPaid?: (
    installment: Installment,
    paidAtDate: string,
    callbacks?: MutationCallbacks
  ) => void;
  onUnmarkAsPaid?: (installment: Installment) => void;
  actionLoading?: boolean;
  actionError?: string | null;
}

const STATUS_VARIANT: Record<
  InstallmentStatusEnum,
  "default" | "success" | "destructive"
> = {
  [InstallmentStatusEnum.Pending]: "default",
  [InstallmentStatusEnum.Paid]: "success",
  [InstallmentStatusEnum.Overdue]: "destructive",
};

const STATUS_LABEL: Record<InstallmentStatusEnum, string> = {
  [InstallmentStatusEnum.Pending]: "Pendente",
  [InstallmentStatusEnum.Paid]: "Pago",
  [InstallmentStatusEnum.Overdue]: "Atrasado",
};

export default function InstallmentRow({
  installment,
  showAdminActions = false,
  onMarkAsPaid,
  onUnmarkAsPaid,
  actionLoading = false,
  actionError,
}: InstallmentRowProps) {
  const isPaid = installment.status === InstallmentStatusEnum.Paid;
  const isOverdue = installment.status === InstallmentStatusEnum.Overdue;

  const handleUnmark = () => {
    if (!onUnmarkAsPaid) return;
    onUnmarkAsPaid(installment);
  };

  const stopRowToggle = (event: React.MouseEvent | React.KeyboardEvent) => {
    event.stopPropagation();
  };

  return (
    <TableRow>
      <TableCell className="w-16">
        <span className="text-sm text-primary-600 dark:text-primary-400">
          #{installment.number.toString().padStart(2, "0")}
        </span>
      </TableCell>
      <TableCell>
        <Currency value={installment.amount} />
      </TableCell>
      <TableCell>
        <DateDisplay
          date={installment.dueDate}
          className={isOverdue ? "text-red-600" : undefined}
        />
      </TableCell>
      <TableCell>
        <Badge
          variant={STATUS_VARIANT[installment.status]}
          className="transition-colors duration-300"
        >
          {STATUS_LABEL[installment.status]}
        </Badge>
      </TableCell>
      <TableCell>
        {isPaid && installment.paidAt ? (
          <DateDisplay
            date={installment.paidAt as string}
            className="font-medium text-green-600"
          />
        ) : null}
      </TableCell>
      {showAdminActions && (
        <TableCell className="w-36 align-middle">
          <div
            className="flex flex-col items-start gap-1"
            onClick={stopRowToggle}
            onKeyDown={stopRowToggle}
          >
            {!isPaid && onMarkAsPaid && (
              <MarkInstallmentAsPaidPopover
                installmentId={installment.id}
                loading={actionLoading}
                error={actionError}
                disabled={actionLoading}
                onConfirm={(paidAtDate, callbacks) =>
                  onMarkAsPaid(installment, paidAtDate, callbacks)
                }
              />
            )}
            {isPaid && onUnmarkAsPaid && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={actionLoading}
                    className="h-auto p-0 text-xs font-light uppercase tracking-widest text-primary-600 hover:text-primary-900"
                  >
                    Desfazer pagamento
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="rounded-none border-primary-200">
                  <AlertDialogHeader>
                    <AlertDialogTitle className="font-light tracking-tight uppercase">
                      Desfazer pagamento
                    </AlertDialogTitle>
                    <AlertDialogDescription className="font-light text-primary-600">
                      A parcela voltará ao status pendente ou atrasado.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="rounded-none uppercase text-xs tracking-widest">
                      Cancelar
                    </AlertDialogCancel>
                    <AlertDialogAction
                      destructive
                      disabled={actionLoading}
                      className="rounded-none uppercase text-xs tracking-widest"
                      onClick={handleUnmark}
                    >
                      Confirmar
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        </TableCell>
      )}
    </TableRow>
  );
}
